import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Globe, Building2, ShieldAlert } from "lucide-react";
import { AppearanceMenu } from "@/components/AppearanceMenu";
import { Logo } from "@/components/Logo";
import { Loading } from "@/components/States";
import { JobMeta } from "@/pages/Jobs";
import { api } from "@/lib/api";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;

export default function CompanyPublic() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { api.get(`/public/companies/${encodeURIComponent(slug)}`).then((r) => setC(r.data)).catch(() => setMissing(true)); }, [slug]);
  return (
    <div className="grid-bg min-h-screen bg-canvas px-4 py-10">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between"><Link to="/"><Logo className="h-10" /></Link><AppearanceMenu /></div>
        <div className="mt-12">
          {missing ? (
            <div data-testid="company-missing" className="flex flex-col items-start gap-3 rounded-3xl border border-line bg-surface p-10">
              <ShieldAlert className="h-8 w-8 text-ink-3" /><p className="font-bold text-ink">Company page not available</p>
              <p className="text-sm text-ink-2">This company doesn't exist or isn't an approved IDDCR partner.</p>
            </div>
          ) : !c ? <Loading /> : (
            <div data-testid="company-page" className="fade-up">
              <div className="flex flex-col gap-6 border-b border-line pb-8 sm:flex-row sm:items-center">
                <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-line bg-surface p-2 shadow-sm">
                  {c.logo_url ? <img data-testid="company-logo" src={`${ORIGIN}${c.logo_url}`} alt={`${c.name} logo`} className="max-h-full max-w-full object-contain" /> : <Building2 className="h-10 w-10 text-slate-300" />}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-aqua">IDDCR partner employer</p>
                  <h1 data-testid="company-name" className="mt-2 text-4xl font-extrabold text-ink sm:text-5xl">{c.name}</h1>
                  {c.website && <a data-testid="company-website" href={c.website} target="_blank" rel="noreferrer nofollow" className="mt-2 inline-flex items-center gap-1.5 text-sm text-aqua hover:underline"><Globe className="h-4 w-4" />{c.website.replace(/^https?:\/\//, "")}</a>}
                </div>
              </div>
              {c.description && <p data-testid="company-description" className="mt-6 max-w-2xl whitespace-pre-line leading-relaxed text-ink-2">{c.description}</p>}
              <h2 className="mb-4 mt-10 text-sm font-semibold uppercase tracking-[0.15em] text-ink-3">Open roles ({c.jobs.length})</h2>
              {c.jobs.length === 0 ? <p data-testid="company-no-jobs" className="rounded-2xl border border-dashed border-line-strong bg-canvas-2/60 p-8 text-sm text-ink-2">No open roles right now.</p> : (
                <div data-testid="company-jobs" className="flex flex-col gap-3">
                  {c.jobs.map((j) => (
                    <div key={j.id} data-testid={`company-job-${j.id}`} className="rounded-2xl border border-line bg-surface p-5">
                      <p className="font-bold text-ink">{j.title}</p>
                      <JobMeta j={{ ...j, company: c.name }} />
                      <p className="mt-3 line-clamp-3 text-sm text-ink-2">{j.description}</p>
                      {j.requirements.length > 0 && <p className="mt-2 text-xs text-ink-3">Requires one of: {j.requirements.map((r) => r.label).join(", ")}</p>}
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-8 text-sm text-ink-2">IDDCR-certified learner? <Link to="/login" data-testid="company-apply-link" className="font-semibold text-aqua hover:underline">Log in to apply</Link></p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
